import type { GroundedPromptMessage, GroundedTokenCounter } from "./grounded-corpus";
import type {
  TransformersLoader,
  TransformersModule,
  TransformersTokenizer,
} from "./transformers-retrieval";

const LFM_TOKENIZER_ID = "LiquidAI/LFM2.5-1.2B-Instruct-ONNX";
const LFM_TOKENIZER_REVISION = "10f72e70abf67ac0fd7ebf15bc5854726891d864";
const tokenizers = new Map<string, Promise<TransformersTokenizer>>();

function huggingFaceModel(value: string) {
  try {
    const url = new URL(value);
    if (url.hostname !== "huggingface.co") return null;
    const parts = url.pathname.split("/").filter(Boolean);
    const resolveIndex = parts.indexOf("resolve");
    if (resolveIndex !== 2 || !parts[0] || !parts[1] || !parts[3]) return null;
    return { id: `${parts[0]}/${parts[1]}`, revision: parts[3] };
  } catch {
    return null;
  }
}

async function defaultTransformersLoader(): Promise<TransformersModule> {
  return import("@huggingface/transformers") as unknown as Promise<TransformersModule>;
}

function loadTokenizer(
  id: string,
  revision: string,
  loadTransformers: TransformersLoader,
) {
  const key = `${id}:${revision}`;
  let pending = tokenizers.get(key);
  if (!pending) {
    pending = loadTransformers()
      .then(({ AutoTokenizer }) => AutoTokenizer.from_pretrained(id, { revision }));
    tokenizers.set(key, pending);
    pending.catch(() => tokenizers.delete(key));
  }
  return pending;
}

export async function createModelTokenCounter(
  modelUrl: string,
  loadTransformers: TransformersLoader = defaultTransformersLoader,
): Promise<GroundedTokenCounter | undefined> {
  const model = huggingFaceModel(modelUrl);
  if (!model) return undefined;
  let tokenizer = await loadTokenizer(model.id, model.revision, loadTransformers);
  let modelType = "";
  if (!tokenizer.chat_template) {
    try {
      const baseUrl = modelUrl.endsWith("/") ? modelUrl : `${modelUrl}/`;
      const response = await fetch(new URL("mlc-chat-config.json", baseUrl));
      if (response.ok) {
        const config = await response.json() as { model_type?: string };
        modelType = config.model_type || "";
      }
    } catch {
    }
  }
  if (!tokenizer.chat_template && modelType === "lfm2") {
    tokenizer = await loadTokenizer(
      LFM_TOKENIZER_ID,
      LFM_TOKENIZER_REVISION,
      loadTransformers,
    );
  }
  if (!tokenizer.chat_template) return undefined;
  return (messages: GroundedPromptMessage[]) => {
    const tokens = tokenizer.apply_chat_template(messages, {
      add_generation_prompt: true,
      tokenize: true,
      return_dict: false,
      return_tensor: false,
    });
    return Array.isArray(tokens) ? tokens.length : Number.POSITIVE_INFINITY;
  };
}

const TOKEN_COUNTER_WAIT_MS = 1000;
const counters = new Map<string, Promise<GroundedTokenCounter | undefined>>();

export function warmModelTokenCounter(
  modelUrl: string,
  loadTransformers: TransformersLoader = defaultTransformersLoader,
) {
  let pending = counters.get(modelUrl);
  if (!pending) {
    pending = createModelTokenCounter(modelUrl, loadTransformers).catch(() => undefined);
    counters.set(modelUrl, pending);
  }
  return pending;
}

export function modelTokenCounterWithin(
  modelUrl: string,
  loadTransformers: TransformersLoader = defaultTransformersLoader,
  waitMs = TOKEN_COUNTER_WAIT_MS,
): Promise<GroundedTokenCounter | undefined> {
  return Promise.race([
    warmModelTokenCounter(modelUrl, loadTransformers),
    new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), waitMs)),
  ]);
}
